import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { empresaAPI, guiasConfigAPI, usuariosAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import usePageTitle from '../lib/usePageTitle';
import ThemeEditorTab from '../components/ThemeEditorTab';
import { 
    Settings, 
    Shield, 
    AlertTriangle, 
    Plus, 
    X, 
    Loader2, 
    Save, 
    Palette, 
    FileText,
    Users,
    Download,
    Upload,
    CheckCircle,
    AlertCircle,
    Trash2,
    Building,
} from 'lucide-react';

export default function ConfiguracionPage() {
    usePageTitle('Configuración');
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [empresa, setEmpresa] = useState(null);
    
    // Tab Empresa
    const [nombreAreaSeguridad, setNombreAreaSeguridad] = useState('');
    const [motivosRechazo, setMotivosRechazo] = useState([]);
    const [nuevoMotivo, setNuevoMotivo] = useState('');
    const [primaryColor, setPrimaryColor] = useState('#F97316');
    const [logoUrl, setLogoUrl] = useState('');

    // Tab Tipos de Documento
    const [tiposDocumento, setTiposDocumento] = useState([]);
    const [etiquetaSeccionEmpresa, setEtiquetaSeccionEmpresa] = useState('Datos empresa');
    const [nuevoTipo, setNuevoTipo] = useState({ id: '', nombre: '' });
    const [savingGuias, setSavingGuias] = useState(false);

    // Tab Usuarios
    const [uploadingUsers, setUploadingUsers] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(null); // { current, total, creados, errores }

    useEffect(() => {
        loadEmpresa();
        loadConfigGuias();
    }, [user]);

    const loadEmpresa = async () => {
        if (!user?.empresa_id) {
            setLoading(false);
            return;
        }
        
        try {
            const response = await empresaAPI.getById(user.empresa_id);
            const data = response.data;
            setEmpresa(data);
            setNombreAreaSeguridad(data.nombre_area_seguridad || 'Protección Industrial');
            setMotivosRechazo(data.motivos_rechazo || []);
            setPrimaryColor(data.primary_color || '#F97316');
            setLogoUrl(data.logo_url || '');
        } catch (err) {
            console.error('Error loading empresa:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadConfigGuias = async () => {
        try {
            const res = await guiasConfigAPI.getConfiguracion();
            const data = res.data;
            if (data.tipos_documento) {
                setTiposDocumento(data.tipos_documento);
            } else {
                setTiposDocumento([
                    { id: 'guia_despacho', nombre: 'Guía de despacho', activo: true },
                    { id: 'factura', nombre: 'Factura', activo: true },
                ]);
            }
            setEtiquetaSeccionEmpresa(data.etiqueta_seccion_empresa || 'Datos empresa');
        } catch (err) {
            console.error('Error cargando config guías:', err);
            setTiposDocumento([
                { id: 'guia_despacho', nombre: 'Guía de despacho', activo: true },
                { id: 'factura', nombre: 'Factura', activo: true },
            ]);
        }
    };

    // === EMPRESA ===
    const addMotivo = () => {
        if (nuevoMotivo.trim() && !motivosRechazo.includes(nuevoMotivo.trim())) {
            setMotivosRechazo([...motivosRechazo, nuevoMotivo.trim()]);
            setNuevoMotivo('');
        }
    };

    const removeMotivo = (motivo) => {
        setMotivosRechazo(motivosRechazo.filter(m => m !== motivo));
    };

    const handleSaveEmpresa = async () => {
        if (!user?.empresa_id) return;
        
        setSaving(true);
        try {
            await empresaAPI.updateConfig(user.empresa_id, {
                nombre_area_seguridad: nombreAreaSeguridad,
                motivos_rechazo: motivosRechazo,
                primary_color: primaryColor,
                logo_url: logoUrl || null,
            });
            toast.success('Configuración de empresa guardada');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    // === TIPOS DE DOCUMENTO ===
    const handleAddTipo = () => {
        if (!nuevoTipo.nombre.trim()) {
            toast.error('Ingrese el nombre del tipo');
            return;
        }
        const id = nuevoTipo.nombre.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        if (tiposDocumento.some(t => t.id === id)) {
            toast.error('Ya existe un tipo con ese nombre');
            return;
        }
        setTiposDocumento([...tiposDocumento, { id, nombre: nuevoTipo.nombre.trim(), activo: true }]);
        setNuevoTipo({ id: '', nombre: '' });
    };

    const handleRemoveTipo = (id) => {
        if (tiposDocumento.filter(t => t.activo).length <= 1 && tiposDocumento.find(t => t.id === id)?.activo) {
            toast.error('Debe haber al menos un tipo activo');
            return;
        }
        setTiposDocumento(tiposDocumento.filter(t => t.id !== id));
    };

    const toggleTipoActivo = (id) => {
        const activosCount = tiposDocumento.filter(t => t.activo && t.id !== id).length;
        if (activosCount === 0 && tiposDocumento.find(t => t.id === id)?.activo) {
            toast.error('Debe haber al menos un tipo activo');
            return;
        }
        setTiposDocumento(tiposDocumento.map(t => 
            t.id === id ? { ...t, activo: !t.activo } : t
        ));
    };

    const handleSaveGuias = async () => {
        if (tiposDocumento.filter(t => t.activo).length === 0) {
            toast.error('Debe haber al menos un tipo de documento activo');
            return;
        }
        setSavingGuias(true);
        try {
            await guiasConfigAPI.updateConfiguracion({
                tipos_documento: tiposDocumento,
                etiqueta_seccion_empresa: etiquetaSeccionEmpresa.trim(),
            });
            toast.success('Configuración de guías guardada');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
        } finally {
            setSavingGuias(false);
        }
    };

    // === CARGA MASIVA USUARIOS ===
    const handleDownloadPlantilla = async () => {
        try {
            const response = await usuariosAPI.getPlantillaExcel();
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'plantilla_usuarios.xlsx';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Plantilla descargada');
        } catch (err) {
            toast.error('Error al descargar la plantilla');
        }
    };

    const handleUploadUsers = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            toast.error('El archivo debe ser Excel (.xlsx o .xls)');
            return;
        }

        setUploadingUsers(true);
        setUploadResult(null);
        setUploadProgress(null);

        try {
            const XLSX = await import('xlsx');
            const data = await file.arrayBuffer();
            const wb = XLSX.read(data, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });

            if (rows.length === 0) {
                toast.error('El archivo está vacío');
                setUploadingUsers(false);
                e.target.value = '';
                return;
            }

            // Normalize header keys to lowercase
            const normalized = rows.map((row, i) => {
                const obj = {};
                for (const [k, v] of Object.entries(row)) {
                    obj[k.toLowerCase().trim()] = v;
                }
                return { ...obj, row_num: i + 2 };
            });

            // Validate required columns
            const firstRow = normalized[0];
            for (const col of ['nombre', 'email', 'rol', 'password']) {
                if (!(col in firstRow)) {
                    toast.error(`Columna requerida faltante: ${col}`);
                    setUploadingUsers(false);
                    e.target.value = '';
                    return;
                }
            }

            // Split into chunks of 100
            const CHUNK_SIZE = 100;
            const chunks = [];
            for (let i = 0; i < normalized.length; i += CHUNK_SIZE) {
                chunks.push(normalized.slice(i, i + CHUNK_SIZE));
            }

            let totalCreados = [];
            let totalErrores = [];

            for (let i = 0; i < chunks.length; i++) {
                setUploadProgress({
                    current: i + 1,
                    total: chunks.length,
                    creados: totalCreados.length,
                    errores: totalErrores.length,
                    filas: `${i * CHUNK_SIZE + 1}-${Math.min((i + 1) * CHUNK_SIZE, normalized.length)} de ${normalized.length}`,
                });

                const batch = chunks[i].map(r => ({
                    nombre: String(r.nombre || ''),
                    email: String(r.email || ''),
                    rol: String(r.rol || ''),
                    password: String(r.password || ''),
                    cargo: r.cargo ? String(r.cargo) : null,
                    row_num: r.row_num,
                }));

                try {
                    const resp = await usuariosAPI.cargaMasivaBatch(batch);
                    totalCreados = [...totalCreados, ...(resp.data.usuarios_creados || [])];
                    totalErrores = [...totalErrores, ...(resp.data.errores || [])];
                } catch (err) {
                    totalErrores.push(`Lote ${i + 1}: ${err.response?.data?.detail || err.message}`);
                }
            }

            const result = {
                message: `Proceso completado. ${totalCreados.length} usuarios creados.`,
                usuarios_creados: totalCreados,
                errores: totalErrores,
                total_procesados: totalCreados.length + totalErrores.length,
            };
            setUploadResult(result);
            setUploadProgress(null);

            if (totalCreados.length > 0) {
                toast.success(`${totalCreados.length} usuarios creados`);
            }
            if (totalErrores.length > 0) {
                toast.warning(`${totalErrores.length} errores encontrados`);
            }
        } catch (err) {
            toast.error(err.message || 'Error al procesar el archivo');
        } finally {
            setUploadingUsers(false);
            e.target.value = '';
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="max-w-4xl mx-auto space-y-6">
                    <Skeleton className="h-10 w-64" />
                    <Skeleton className="h-64" />
                    <Skeleton className="h-64" />
                </div>
            </Layout>
        );
    }

    if (!empresa) {
        return (
            <Layout>
                <div className="text-center py-16">
                    <Settings className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                    <h2 className="font-heading text-xl font-bold">Sin empresa asignada</h2>
                    <p className="text-muted-foreground mt-2">
                        Contacte al administrador para asignar una empresa a su cuenta
                    </p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
                {/* Header */}
                <div>
                    <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
                        Configuración
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {empresa.nombre}
                    </p>
                </div>

                <Tabs defaultValue="empresa" className="space-y-6">
                    <TabsList className="grid grid-cols-4 w-full max-w-lg">
                        <TabsTrigger value="empresa" className="flex items-center gap-2">
                            <Building className="h-4 w-4" />
                            <span className="hidden sm:inline">Empresa</span>
                        </TabsTrigger>
                        <TabsTrigger value="guias" className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            <span className="hidden sm:inline">Guías</span>
                        </TabsTrigger>
                        <TabsTrigger value="usuarios" className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            <span className="hidden sm:inline">Usuarios</span>
                        </TabsTrigger>
                        <TabsTrigger value="apariencia" className="flex items-center gap-2" data-testid="tab-apariencia">
                            <Palette className="h-4 w-4" />
                            <span className="hidden sm:inline">Apariencia</span>
                        </TabsTrigger>
                    </TabsList>

                    {/* TAB: EMPRESA */}
                    <TabsContent value="empresa" className="space-y-6">
                        <div className="flex justify-end">
                            <Button onClick={handleSaveEmpresa} disabled={saving}>
                                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                                Guardar
                            </Button>
                        </div>

                        {/* Área de Seguridad */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Shield className="h-5 w-5" />
                                    Área de Seguridad
                                </CardTitle>
                                <CardDescription>
                                    Nombre del área que aparecerá en el punto de salida
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    <Label>Nombre del Área</Label>
                                    <Input
                                        value={nombreAreaSeguridad}
                                        onChange={(e) => setNombreAreaSeguridad(e.target.value)}
                                        placeholder="Ej: Protección Industrial, Portería"
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Motivos de Rechazo */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <AlertTriangle className="h-5 w-5" />
                                    Motivos de Rechazo
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex gap-2">
                                    <Input
                                        value={nuevoMotivo}
                                        onChange={(e) => setNuevoMotivo(e.target.value)}
                                        placeholder="Nuevo motivo de rechazo"
                                        onKeyDown={(e) => e.key === 'Enter' && addMotivo()}
                                    />
                                    <Button type="button" onClick={addMotivo} variant="outline">
                                        <Plus className="h-4 w-4" />
                                    </Button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {motivosRechazo.map((motivo, i) => (
                                        <div key={i} className="flex items-center gap-1 bg-muted px-3 py-1 rounded-full">
                                            <span className="text-sm">{motivo}</span>
                                            <button onClick={() => removeMotivo(motivo)} className="text-destructive hover:text-destructive/80">
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Personalización */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Palette className="h-5 w-5" />
                                    Personalización
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Color Principal</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="color"
                                            value={primaryColor}
                                            onChange={(e) => setPrimaryColor(e.target.value)}
                                            className="w-16 h-10 p-1"
                                        />
                                        <Input
                                            value={primaryColor}
                                            onChange={(e) => setPrimaryColor(e.target.value)}
                                            placeholder="#F97316"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>URL del Logo</Label>
                                    <Input
                                        value={logoUrl}
                                        onChange={(e) => setLogoUrl(e.target.value)}
                                        placeholder="https://..."
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* TAB: GUÍAS */}
                    <TabsContent value="guias" className="space-y-6">
                        <div className="flex justify-end">
                            <Button onClick={handleSaveGuias} disabled={savingGuias}>
                                {savingGuias ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                                Guardar
                            </Button>
                        </div>

                        {/* Tipos de Documento */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="h-5 w-5" />
                                    Tipos de Documento
                                </CardTitle>
                                <CardDescription>
                                    Tipos de documento disponibles al crear guías
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {tiposDocumento.map(tipo => (
                                    <div key={tipo.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <Switch
                                                checked={tipo.activo}
                                                onCheckedChange={() => toggleTipoActivo(tipo.id)}
                                            />
                                            <div>
                                                <p className="font-medium">{tipo.nombre}</p>
                                                <p className="text-xs text-muted-foreground">ID: {tipo.id}</p>
                                            </div>
                                        </div>
                                        <Button variant="ghost" size="icon" onClick={() => handleRemoveTipo(tipo.id)} className="text-destructive">
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}

                                <div className="flex gap-2 pt-4 border-t">
                                    <Input
                                        value={nuevoTipo.nombre}
                                        onChange={(e) => setNuevoTipo({ ...nuevoTipo, nombre: e.target.value })}
                                        placeholder="Nombre del nuevo tipo"
                                        onKeyDown={(e) => e.key === 'Enter' && handleAddTipo()}
                                    />
                                    <Button type="button" onClick={handleAddTipo} variant="outline">
                                        <Plus className="h-4 w-4 mr-1" />
                                        Agregar
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Etiquetas */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Etiquetas de Secciones</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    <Label>Sección de Datos de Destino/Empresa</Label>
                                    <Input
                                        value={etiquetaSeccionEmpresa}
                                        onChange={(e) => setEtiquetaSeccionEmpresa(e.target.value)}
                                        placeholder="Ej: Datos empresa, Cliente"
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* TAB: USUARIOS */}
                    <TabsContent value="usuarios" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Users className="h-5 w-5" />
                                    Carga Masiva de Usuarios
                                </CardTitle>
                                <CardDescription>
                                    Cree múltiples usuarios usando un archivo Excel
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Paso 1 */}
                                <div className="space-y-2">
                                    <Label className="font-medium">Paso 1: Descargar plantilla</Label>
                                    <Button variant="outline" onClick={handleDownloadPlantilla} className="w-full justify-start">
                                        <Download className="h-4 w-4 mr-2" />
                                        Descargar plantilla Excel
                                    </Button>
                                    <p className="text-xs text-muted-foreground">
                                        La plantilla incluye instrucciones y ejemplos
                                    </p>
                                </div>

                                {/* Paso 2 */}
                                <div className="space-y-2 pt-4 border-t">
                                    <Label className="font-medium">Paso 2: Cargar usuarios</Label>
                                    <div className="border-2 border-dashed border-primary/50 rounded-lg p-6 text-center bg-primary/5">
                                        <input
                                            type="file"
                                            id="uploadUsers"
                                            accept=".xlsx,.xls"
                                            onChange={handleUploadUsers}
                                            className="hidden"
                                            disabled={uploadingUsers}
                                        />
                                        <label htmlFor="uploadUsers" className="cursor-pointer flex flex-col items-center gap-2">
                                            {uploadingUsers ? (
                                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                            ) : (
                                                <Upload className="h-8 w-8 text-primary" />
                                            )}
                                            <span className="text-sm font-medium text-primary">
                                                {uploadingUsers ? 'Procesando...' : 'Cargar archivo Excel'}
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                {/* Progreso de carga por lotes */}
                                {uploadProgress && (
                                    <div className="space-y-2 pt-4 border-t" data-testid="upload-progress">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="font-medium">Procesando lote {uploadProgress.current} de {uploadProgress.total}</span>
                                            <span className="text-muted-foreground">Filas {uploadProgress.filas}</span>
                                        </div>
                                        <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                                            <div
                                                className="bg-primary h-full rounded-full transition-all duration-300"
                                                style={{ width: `${Math.round((uploadProgress.current / uploadProgress.total) * 100)}%` }}
                                            />
                                        </div>
                                        <div className="flex gap-4 text-xs text-muted-foreground">
                                            <span className="text-green-600">{uploadProgress.creados} creados</span>
                                            {uploadProgress.errores > 0 && <span className="text-red-500">{uploadProgress.errores} errores</span>}
                                        </div>
                                    </div>
                                )}

                                {/* Resultados */}
                                {uploadResult && (
                                    <div className="space-y-3 pt-4 border-t">
                                        {uploadResult.usuarios_creados?.length > 0 && (
                                            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-lg p-3">
                                                <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                                                    <CheckCircle className="h-4 w-4" />
                                                    {uploadResult.usuarios_creados.length} usuarios creados
                                                </div>
                                                <ul className="text-sm text-green-600 space-y-1 ml-6">
                                                    {uploadResult.usuarios_creados.slice(0, 5).map((u, i) => (
                                                        <li key={i}>• {u.nombre} ({u.email})</li>
                                                    ))}
                                                    {uploadResult.usuarios_creados.length > 5 && (
                                                        <li>... y {uploadResult.usuarios_creados.length - 5} más</li>
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                        {uploadResult.errores?.length > 0 && (
                                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-lg p-3">
                                                <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                                                    <AlertCircle className="h-4 w-4" />
                                                    {uploadResult.errores.length} errores
                                                </div>
                                                <ul className="text-sm text-red-600 space-y-1 ml-6">
                                                    {uploadResult.errores.slice(0, 5).map((err, i) => (
                                                        <li key={i}>• {err}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Roles */}
                                <div className="pt-4 border-t">
                                    <Label className="font-medium mb-2 block">Roles disponibles</Label>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                            <span><strong>admin</strong> - Ve todas las guías</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                            <span><strong>gerente</strong> - Ve guías asignadas</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                            <span><strong>solicitante</strong> - Solo sus guías</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                                            <span><strong>seguridad</strong> - Ve todas (PI)</span>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* TAB: APARIENCIA */}
                    <TabsContent value="apariencia" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Palette className="h-5 w-5" />
                                    Apariencia
                                </CardTitle>
                                <CardDescription>
                                    Personalice colores, modo claro/oscuro y logo de la empresa
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <ThemeEditorTab />
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </Layout>
    );
}
